import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert
} from '@mui/material'
import {
  Storage,
  Add,
  Edit,
  Delete,
  Visibility,
  Download,
  Upload,
  Refresh
} from '@mui/icons-material'
import ICDMasterManagement from '../components/mrd/MasterData/ICDMasterManagement'
import DoctorMasterManagement from '../components/mrd/MasterData/DoctorMasterManagement'
import WardMasterManagement from '../components/mrd/MasterData/WardMasterManagement'
import DeficiencyMasterManagement from '../components/mrd/deficiency/DeficiencyMaster'
import { useMRD } from '../contexts/Mrdcontexts'
import { useSnackbar } from 'notistack'

export const MasterDataPage = () => {
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(false)
  const { masterDataStats, fetchMasterDataStats } = useMRD()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    loadMasterDataStats()
  }, [])

  const loadMasterDataStats = async () => {
    setLoading(true)
    try {
      await fetchMasterDataStats()
    } catch (error) {
      enqueueSnackbar('Failed to load master data statistics', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const masterDataTabs = [
    {
      label: 'ICD Codes',
      icon: <Storage />,
      component: <ICDMasterManagement />,
      description: 'Manage ICD-10, ICD-11, and ICD-10-PCS codes'
    },
    {
      label: 'Deficiencies',
      icon: <Storage />,
      component: <DeficiencyMasterManagement />,
      description: 'Manage deficiency types and categories'
    },
    // {
    //   label: 'Doctors',
    //   icon: <Storage />,
    //   component: <DoctorMasterManagement />,
    //   description: 'Manage doctor profiles and specializations'
    // },
    // {
    //   label: 'Wards',
    //   icon: <Storage />,
    //   component: <WardMasterManagement />,
    //   description: 'Manage ward configurations and bed management'
    // }
  ]

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Master Data Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage system master data including ICD codes, doctors, wards, and deficiencies
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadMasterDataStats}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {/* Quick Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  ICD Codes
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {masterDataStats?.summary?.activeICDCodes || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active codes ({masterDataStats?.icdStatistics?.icd10 || 0} ICD-10, {masterDataStats?.icdStatistics?.icd11 || 0} ICD-11)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total: {masterDataStats?.summary?.totalICDCodes || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Doctors
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {masterDataStats?.summary?.activeDoctors || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active doctor profiles
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total: {masterDataStats?.summary?.totalDoctors || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main" gutterBottom>
                  Wards
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {masterDataStats?.summary?.activeWards || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active ward configurations
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total: {masterDataStats?.summary?.totalWards || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main" gutterBottom>
                  Deficiencies
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {masterDataStats?.summary?.activeDeficiencyMasters || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deficiency master types
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {masterDataStats?.deficiencyStatistics?.critical || 0} Critical
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {masterDataTabs.map((tab, index) => (
            <Tab 
              key={index}
              label={tab.label} 
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3 }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {masterDataTabs[tabValue].label} Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {masterDataTabs[tabValue].description}
          </Typography>
        </Box>

        {masterDataTabs[tabValue].component}
      </Paper>
    </Container>
  )
}

